// PRD.318 — Dental Treatment: Treatment Plan + Treatment Done.
//
// ONE section, two stacked subsections:
//
//   Treatment Plan  → dental_treatments (patient-longitudinal, planned/in-progress)
//   Treatment Done  → dental_treatment_records (encounter-scoped, what happened)
//
// Both are visible at once: recording performed work is read against the plan
// directly above it, and "Record done" drops its draft into the section below
// without the clinician losing sight of the plan it came from.
//
// Marking a plan done does NOT move or copy its row: it creates a performed
// record for this encounter and then updates the plan's lifecycle status
// (PRD §10). A plan may accumulate several performed records across visits
// while staying in_progress.
//
// Suggestions carry TOOTH CONTEXT and a provenance label only. Nothing maps a
// chart condition or diagnosis to a treatment (PRD §16A.3) — the clinician
// types it. Both subsections show the strip: PRD §16A.7 confined it to
// Treatment Plan, but unplanned work is documented against the same teeth, so
// re-picking them by hand was pure friction. The safeguard that matters is
// unchanged — a tap preselects teeth and leaves the treatment field empty.
//
// Reuse: `HistoryEntryEditor` (PRD.314/316) for the autosaving, dictating notes
// field; `ToothPickerModal` (PRD.316) for tooth selection; `summarizeDiagnosisTeeth`
// (PRD.317) for the compact `16 · 17 · 18 +3` label.

import { useCallback, useMemo, useRef, useState } from 'react';
import {
  ACTIVE_PLAN_STATUSES,
  DENTAL_TREATMENT_STATUS_LABELS,
  buildChartSuggestions,
  dentalToothLinkId,
  diffTeeth,
  filterUsedTreatmentSuggestions,
  formatSurfaceInitials,
  getMedicalHistorySettings,
  groupToothLinks,
  groupTreatmentSuggestions,
  parseToothStatesMap,
  qAppConfig,
  qAppointmentServices,
  resolveTreatmentLabel,
  qDentalTreatmentRecordTeethByPatient,
  qDentalTreatmentRecordsByPatient,
  qDentalTreatmentTeethByPatient,
  qDentalTreatmentsByPatient,
  qDiagnosesByEncounter,
  qDiagnosisTeethByEncounter,
  qOdontogramChartsByEncounter,
  groupTeethByDiagnosis,
  readTreatmentStatus,
  selectActivePlan,
  selectPerformedRecords,
  selectRecordsForTreatment,
  summarizeDiagnosisTeeth,
  type AppConfigRow,
  type AppointmentServiceRow,
  type TreatmentEntryValue,
  type DentalTreatmentRecordRow,
  type DentalTreatmentRow,
  type DentalTreatmentStatus,
  type DiagnosisToothRow,
  type OdontogramChartRow,
  type PatientDiagnosisEntryRow,
  type PatientEncounterRow,
  type TreatmentSuggestion,
} from '@helios/data-access';
import { useAuth } from '@helios/auth-firebase-web';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';
import { NavIcon, useBrandTokens } from '../../../shell/shared';
import { HistoryEntryEditor } from './HistoryEntryEditor';
import { ToothPickerModal } from './ToothPickerModal';
import { TreatmentEntryInput } from './TreatmentEntryInput';

/** Border + padding above a compact field's first line — see ClinicalFindingsSection. */
const FIRST_LINE_OFFSET = 7;

/**
 * Provenance captured when a plan draft is opened from a suggestion, written
 * with the row so the suggestion can later be suppressed by exact identity.
 */
interface DraftMeta {
  source: string;
  sourceRef: string | null;
  diagnosisId: string | null;
  sourceLabel: string | null;
}

/**
 * Subsection heading inside the Treatment card. Subordinate to the card title
 * ("Treatment") but stronger than the muted labels used for suggestion groups,
 * so Plan and Done read as two parts of one section rather than two cards.
 */
function SubsectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        fontSize: 'var(--font-size-sm)',
        fontWeight: 'var(--font-weight-semi)',
        lineHeight: 'var(--line-height-sm)',
        color: 'var(--text)',
      }}
    >
      {children}
    </span>
  );
}

/** Which record a tooth picker is currently editing. */
type PickerTarget = { kind: 'plan' | 'record'; id: string } | null;

export function DentalTreatmentSection({
  encounter,
  patientId,
}: {
  encounter: PatientEncounterRow;
  patientId: string;
}) {
  const { db } = usePowerSyncDb();
  const { claims, user } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';

  const [picker, setPicker] = useState<PickerTarget>(null);

  /**
   * Unpersisted rows opened by "+ Add Treatment" / "+ Add Treatment Done".
   * They become records on their first meaningful save, so an abandoned click
   * never leaves a blank treatment behind — the PRD.316 rule.
   */
  const [planDraft, setPlanDraft] = useState<{ id: string; teeth: string[] } | null>(null);
  /** The service catalogue, for the treatment typeahead. Read-only here. */
  const { data: serviceRows = [] } =
    usePowerSyncQuery<AppointmentServiceRow>(qAppointmentServices());
  const [doneDraft, setDoneDraft] = useState<{
    id: string;
    teeth: string[];
    treatmentId: string | null;
    /** Pre-filled from the plan when opened via "Record done". */
    treatment: string;
    serviceId: string | null;
  } | null>(null);
  const pendingRef = useRef(
    new Map<
      string,
      {
        kind: 'plan' | 'record';
        teeth: string[];
        treatmentId: string | null;
        /** Set when the clinician picked a Service; null for free text. */
        serviceId: string | null;
      }
    >(),
  );
  const draftMetaRef = useRef(new Map<string, DraftMeta>());

  // ── Reads ──

  const { data: appConfigRows = [] } = usePowerSyncQuery<AppConfigRow>(qAppConfig());
  const voiceSettings = useMemo(
    () => getMedicalHistorySettings(appConfigRows[0] ?? null),
    [appConfigRows],
  );

  const { data: treatmentRows = [] } = usePowerSyncQuery<DentalTreatmentRow>(
    qDentalTreatmentsByPatient(patientId),
  );
  const { data: planToothRows = [] } = usePowerSyncQuery<{
    id: string;
    tooth: string | null;
    dental_treatment_id: string | null;
  }>(qDentalTreatmentTeethByPatient(patientId));
  const { data: recordRows = [] } = usePowerSyncQuery<DentalTreatmentRecordRow>(
    qDentalTreatmentRecordsByPatient(patientId),
  );
  const { data: recordToothRows = [] } = usePowerSyncQuery<{
    id: string;
    tooth: string | null;
    treatment_record_id: string | null;
  }>(qDentalTreatmentRecordTeethByPatient(patientId));

  const planTeeth = useMemo(
    () => groupToothLinks(planToothRows, (r) => r.dental_treatment_id),
    [planToothRows],
  );
  const recordTeeth = useMemo(
    () => groupToothLinks(recordToothRows, (r) => r.treatment_record_id),
    [recordToothRows],
  );

  const activePlan = useMemo(() => selectActivePlan(treatmentRows), [treatmentRows]);
  // Treatment Done shows THIS encounter's work; a plan row's own history is
  // rendered inline beneath it and spans encounters.
  const encounterRecords = useMemo(
    () => selectPerformedRecords(recordRows.filter((r) => r.encounter_id === encounter.id)),
    [recordRows, encounter.id],
  );

  // ── Suggestions (Treatment Plan only — PRD §16A.7) ──

  const { data: chartRows = [] } = usePowerSyncQuery<OdontogramChartRow>(
    qOdontogramChartsByEncounter(encounter.id),
  );
  const { data: diagnosisRows = [] } = usePowerSyncQuery<PatientDiagnosisEntryRow>(
    qDiagnosesByEncounter(encounter.id),
  );
  const { data: diagnosisToothRows = [] } = usePowerSyncQuery<DiagnosisToothRow>(
    qDiagnosisTeethByEncounter(encounter.id),
  );

  const suggestions = useMemo(() => {
    const all: TreatmentSuggestion[] = [];

    // FROM CHART — reuses PRD.316's chart-suggestion derivation, so there is no
    // second reading of `tooth_states`. The condition label rides along as
    // provenance only.
    for (const chart of chartRows) {
      for (const s of buildChartSuggestions(chart.id, parseToothStatesMap(chart.tooth_states))) {
        all.push({
          ref: s.ref,
          source: 'chart',
          teeth: [s.tooth],
          label: s.label,
          surfaces: formatSurfaceInitials(s.surfaces),
        });
      }
    }

    // FROM DIAGNOSIS — only diagnoses that actually carry tooth context are
    // useful here, since the whole point is preselecting teeth. Degrades to
    // nothing when PRD.317 has no associations (PRD §16A.6).
    const teethByDiagnosis = groupTeethByDiagnosis(diagnosisToothRows);
    for (const d of diagnosisRows) {
      const teeth = teethByDiagnosis.get(d.id) ?? [];
      if (teeth.length === 0) continue;
      all.push({
        ref: d.id,
        source: 'diagnosis',
        teeth,
        label: d.label,
        surfaces: '',
        diagnosisId: d.id,
      });
    }

    return groupTreatmentSuggestions(filterUsedTreatmentSuggestions(all, treatmentRows));
  }, [chartRows, diagnosisRows, diagnosisToothRows, treatmentRows]);

  // ── Writes ──

  const insertToothLinks = useCallback(
    async (table: 'plan' | 'record', parentId: string, teeth: readonly string[]) => {
      if (!db) return;
      const now = new Date().toISOString();
      const sql =
        table === 'plan'
          ? `INSERT INTO dental_treatment_teeth
               (id, org_id, dental_treatment_id, patient_id, tooth, created_at, updated_at)
             VALUES (?, ?, ?, ?, ?, ?, ?)`
          : `INSERT INTO dental_treatment_record_teeth
               (id, org_id, treatment_record_id, patient_id, tooth, created_at, updated_at)
             VALUES (?, ?, ?, ?, ?, ?, ?)`;
      for (const tooth of teeth) {
        await db.execute(sql, [
          dentalToothLinkId(parentId, tooth),
          orgId,
          parentId,
          patientId,
          tooth,
          now,
          now,
        ]);
      }
    },
    [db, orgId, patientId],
  );

  /** Persist a tooth selection as a minimal diff — unchanged teeth write nothing. */
  const saveTeeth = useCallback(
    async (kind: 'plan' | 'record', parentId: string, next: string[]) => {
      if (!db) return;
      const current = (kind === 'plan' ? planTeeth.get(parentId) : recordTeeth.get(parentId)) ?? [];
      const { added, removed } = diffTeeth(current, next);
      const table = kind === 'plan' ? 'dental_treatment_teeth' : 'dental_treatment_record_teeth';
      for (const tooth of removed) {
        await db.execute(`DELETE FROM ${table} WHERE id = ?`, [dentalToothLinkId(parentId, tooth)]);
      }
      await insertToothLinks(kind, parentId, added);
    },
    [db, planTeeth, recordTeeth, insertToothLinks],
  );

  /**
   * Autosave a plan item's treatment text. A draft is INSERTed on its first
   * meaningful text together with any preselected teeth and provenance.
   */
  const savePlanTreatment = useCallback(
    async (text: string, id: string) => {
      if (!db) return;
      const trimmed = text.trim();
      const now = new Date().toISOString();
      const pending = pendingRef.current.get(id);

      if (pending?.kind === 'plan') {
        if (!trimmed) return;
        const meta = draftMetaRef.current.get(id);
        await db.execute(
          `INSERT INTO dental_treatments
             (id, org_id, clinic_id, patient_id, created_encounter_id, treatment, service_id,
              status, source, source_ref, source_diagnosis_id, source_label,
              created_by_user_id, created_at, updated_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            id,
            orgId,
            null,
            patientId,
            encounter.id,
            trimmed,
            pending.serviceId,
            'planned',
            meta?.source ?? 'manual',
            meta?.sourceRef ?? null,
            meta?.diagnosisId ?? null,
            meta?.sourceLabel ?? null,
            user?.uid ?? null,
            now,
            now,
          ],
        );
        if (pending.teeth.length > 0) await insertToothLinks('plan', id, pending.teeth);
        pendingRef.current.delete(id);
        draftMetaRef.current.delete(id);
        setPlanDraft((d) => (d?.id === id ? null : d));
        return;
      }

      if (!trimmed) return;
      await db.execute(`UPDATE dental_treatments SET treatment = ?, updated_at = ? WHERE id = ?`, [
        trimmed,
        now,
        id,
      ]);
    },
    [db, orgId, patientId, encounter.id, user?.uid, insertToothLinks],
  );

  /** Autosave a performed record's treatment text (INSERT on first content). */
  const saveRecordTreatment = useCallback(
    async (text: string, id: string) => {
      if (!db) return;
      const trimmed = text.trim();
      const now = new Date().toISOString();
      const pending = pendingRef.current.get(id);

      if (pending?.kind === 'record') {
        if (!trimmed) return;
        await db.execute(
          `INSERT INTO dental_treatment_records
             (id, org_id, clinic_id, patient_id, encounter_id, dental_treatment_id,
              treatment, service_id, notes, performed_at, performed_by, created_at, updated_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            id,
            orgId,
            null,
            patientId,
            encounter.id,
            pending.treatmentId,
            trimmed,
            pending.serviceId,
            null,
            now,
            user?.uid ?? null,
            now,
            now,
          ],
        );
        if (pending.teeth.length > 0) await insertToothLinks('record', id, pending.teeth);
        pendingRef.current.delete(id);
        setDoneDraft((d) => (d?.id === id ? null : d));
        return;
      }

      if (!trimmed) return;
      await db.execute(
        `UPDATE dental_treatment_records SET treatment = ?, updated_at = ? WHERE id = ?`,
        [trimmed, now, id],
      );
    },
    [db, orgId, patientId, encounter.id, user?.uid, insertToothLinks],
  );

  /** Notes only ever belong to a persisted performed record. */
  const saveRecordNotes = useCallback(
    async (text: string, id: string) => {
      if (!db || pendingRef.current.has(id)) return;
      await db.execute(
        `UPDATE dental_treatment_records SET notes = ?, updated_at = ? WHERE id = ?`,
        [text.trim() ? text : null, new Date().toISOString(), id],
      );
    },
    [db],
  );

  const setStatus = useCallback(
    async (id: string, status: DentalTreatmentStatus) => {
      if (!db) return;
      // Lifecycle only — the row never moves or is copied (PRD §9.2).
      await db.execute(`UPDATE dental_treatments SET status = ?, updated_at = ? WHERE id = ?`, [
        status,
        new Date().toISOString(),
        id,
      ]);
    },
    [db],
  );

  /**
   * Record work performed against a plan (PRD §10).
   *
   * Opens a Treatment Done draft below, pre-filled with the plan's treatment
   * text and tooth scope. The clinician confirms the actual performed scope —
   * the planned teeth are a starting point, never an assumption that all of
   * them were treated. Status is changed separately,
   * because a visit may be one step of a multi-visit treatment.
   */
  const recordPerformed = useCallback(
    (t: DentalTreatmentRow) => {
      const id = crypto.randomUUID();
      const teeth = planTeeth.get(t.id) ?? [];
      // Plan → Done carries the plan's service reference forward, so the
      // clinician never re-selects it (PRD path A).
      pendingRef.current.set(id, {
        kind: 'record',
        teeth: [...teeth],
        treatmentId: t.id,
        serviceId: t.service_id ?? null,
      });
      // Pre-fill the label too, not just the reference: an empty field asked
      // the clinician to re-enter treatment they had already documented.
      setDoneDraft({
        id,
        teeth: [...teeth],
        treatmentId: t.id,
        treatment: resolveTreatmentLabel(t),
        serviceId: t.service_id ?? null,
      });
    },
    [planTeeth],
  );

  const startPlanDraft = useCallback((teeth: string[], meta?: DraftMeta) => {
    const id = crypto.randomUUID();
    pendingRef.current.set(id, {
      kind: 'plan',
      teeth: [...teeth],
      treatmentId: null,
      serviceId: null,
    });
    if (meta) draftMetaRef.current.set(id, meta);
    setPlanDraft({ id, teeth: [...teeth] });
  }, []);

  const startDoneDraft = useCallback((teeth: string[] = []) => {
    const id = crypto.randomUUID();
    pendingRef.current.set(id, {
      kind: 'record',
      teeth: [...teeth],
      treatmentId: null,
      serviceId: null,
    });
    // No provenance is stored on a performed record: `dental_treatment_records`
    // has no source columns, so a suggestion used here is not suppressed. PRD
    // §16A.5 prefers leaving a suggestion visible over inventing a mapping.
    setDoneDraft({ id, teeth: [...teeth], treatmentId: null, treatment: '', serviceId: null });
  }, []);

  const removePlan = useCallback(
    async (id: string) => {
      if (pendingRef.current.has(id)) {
        pendingRef.current.delete(id);
        draftMetaRef.current.delete(id);
        setPlanDraft((d) => (d?.id === id ? null : d));
        return;
      }
      if (!db) return;
      // Cancelled, not deleted: performed records already reference this plan
      // and must keep their provenance (PRD §20).
      await setStatus(id, 'cancelled');
    },
    [db, setStatus],
  );

  const removeRecord = useCallback(
    async (id: string) => {
      if (pendingRef.current.has(id)) {
        pendingRef.current.delete(id);
        setDoneDraft((d) => (d?.id === id ? null : d));
        return;
      }
      if (!db) return;
      try {
        await db.execute(
          `DELETE FROM dental_treatment_record_teeth WHERE treatment_record_id = ?`,
          [id],
        );
      } catch (err) {
        console.warn('[dental-treatment] could not clear performed tooth scope', err);
      }
      await db.execute(`DELETE FROM dental_treatment_records WHERE id = ?`, [id]);
    },
    [db],
  );

  // ── Render helpers ──

  const planRows = useMemo(() => {
    const list = activePlan.map((t) => ({ row: t, isDraft: false }));
    if (planDraft && !activePlan.some((t) => t.id === planDraft.id)) {
      list.push({ row: { id: planDraft.id } as DentalTreatmentRow, isDraft: true });
    }
    return list;
  }, [activePlan, planDraft]);

  const doneRows = useMemo(() => {
    const list = encounterRecords.map((r) => ({ row: r, isDraft: false }));
    if (doneDraft && !encounterRecords.some((r) => r.id === doneDraft.id)) {
      list.push({ row: { id: doneDraft.id } as DentalTreatmentRecordRow, isDraft: true });
    }
    return list;
  }, [encounterRecords, doneDraft]);

  const toothButton = (
    kind: 'plan' | 'record',
    id: string,
    teeth: readonly string[],
    isDraft: boolean,
  ) => {
    const summary = summarizeDiagnosisTeeth([...teeth]);
    return (
      <button
        className="pc-finding-context"
        onClick={() => setPicker({ kind, id })}
        title={
          summary.hiddenCount > 0
            ? `Teeth: ${summary.fullLabel}`
            : 'Choose teeth for this treatment'
        }
        aria-label={
          summary.all.length > 0
            ? `Teeth ${summary.fullLabel}. Choose teeth`
            : 'No tooth association. Choose teeth'
        }
        style={{
          padding: 0,
          paddingTop: FIRST_LINE_OFFSET,
          border: 'none',
          background: 'transparent',
          color: summary.label ? 'var(--text)' : 'var(--text-muted)',
          fontSize: 'var(--font-size-xs)',
          lineHeight: 'var(--line-height-body)',
          fontWeight: summary.label ? 600 : 400,
          cursor: 'pointer',
          fontFamily: 'inherit',
          textAlign: 'left',
          whiteSpace: 'nowrap',
          alignSelf: 'flex-start',
          borderBottom: isDraft ? '1px dashed var(--border)' : undefined,
        }}
      >
        {summary.label || 'General'}
      </button>
    );
  };

  const addButton = (label: string, onClick: () => void) => (
    <div>
      <button
        onClick={onClick}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 12px',
          borderRadius: 'var(--radius-sm)',
          border: `1px solid ${primary}`,
          background: 'transparent',
          color: primary,
          fontSize: 'var(--font-size-xs)',
          fontWeight: 'var(--font-weight-medium)',
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        <NavIcon name="plus" />
        {label}
      </button>
    </div>
  );

  /**
   * The `Suggested` strip, rendered by BOTH subsections.
   *
   * PRD §16A.7 originally confined suggestions to Treatment Plan. Showing them
   * in Treatment Done as well is a deliberate product decision: unplanned work
   * is documented against the same teeth, and re-picking them by hand is the
   * friction the panel exists to remove.
   *
   * The safeguard is unchanged and is what actually matters — a suggestion
   * carries TOOTH CONTEXT and a provenance label, never a treatment. Tapping
   * one in Treatment Done opens a performed-record draft on those teeth with
   * the treatment field still empty (PRD §16A.3).
   *
   * `onPick` decides which draft is opened, so the two subsections share one
   * implementation and one appearance.
   */
  const suggestionStrip = (onPick: (teeth: string[], meta: DraftMeta) => void) => {
    if (suggestions.length === 0) return null;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
        <span
          style={{
            fontSize: 'var(--font-size-xs)',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--text-muted)',
          }}
        >
          Suggested from Chart
        </span>
        {suggestions.map((group) => (
          <div key={group.source} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span
              style={{
                fontSize: 'calc(var(--font-size-xs) * 0.85)',
                color: 'var(--text-placeholder, var(--text-muted))',
                textTransform: 'uppercase',
                letterSpacing: 0.4,
              }}
            >
              {group.title}
            </span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
              {group.items.map((s) => (
                <button
                  key={s.ref}
                  onClick={() =>
                    onPick(s.teeth, {
                      source: s.source,
                      sourceRef: s.source === 'chart' ? s.ref : null,
                      diagnosisId: s.diagnosisId ?? null,
                      sourceLabel: s.label,
                    })
                  }
                  title="Use this tooth context — you still choose the treatment"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '4px 10px',
                    borderRadius: 'var(--radius-full)',
                    border: `1px dashed ${primary}`,
                    background: 'transparent',
                    color: primary,
                    fontSize: 'var(--font-size-xs)',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  <span style={{ fontWeight: 600 }}>+</span>
                  {[s.teeth.join(' · '), s.surfaces || null, s.label].filter(Boolean).join(' · ')}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const pickerTeeth = picker
    ? (pendingRef.current.get(picker.id)?.teeth ??
      (picker.kind === 'plan' ? planTeeth.get(picker.id) : recordTeeth.get(picker.id)) ??
      [])
    : [];

  return (
    <div
      style={{
        padding: 'var(--sp-4) var(--sp-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-4)',
      }}
    >
      {/* ── Treatment Plan ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
        <SubsectionHeading>Treatment Plan</SubsectionHeading>
        {suggestionStrip((teeth, meta) => startPlanDraft(teeth, meta))}

        {planRows.length === 0 && (
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
            No planned treatment.
          </div>
        )}

        {planRows.map(({ row, isDraft }) => {
          const teeth = isDraft ? (planDraft?.teeth ?? []) : (planTeeth.get(row.id) ?? []);
          const status = readTreatmentStatus(row.status);
          const visits = isDraft ? [] : selectRecordsForTreatment(recordRows, row.id);
          return (
            <div
              key={row.id}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                paddingBottom: 'var(--sp-2)',
                borderBottom: '1px solid var(--border)',
              }}
            >
              <div
                className="pc-finding-row"
                style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--sp-2)' }}
              >
                {toothButton('plan', row.id, teeth, isDraft)}

                <div style={{ flex: 1, minWidth: 0 }}>
                  {isDraft ? (
                    <TreatmentEntryInput
                      services={serviceRows}
                      placeholder="Search services or enter treatment…"
                      primary={primary}
                      autoFocus
                      onCommit={(value: TreatmentEntryValue) => {
                        // Record the catalogue reference (or null) alongside the
                        // snapshotted label, then persist through the normal path.
                        const pending = pendingRef.current.get(row.id);
                        if (pending) pending.serviceId = value.serviceId;
                        void savePlanTreatment(value.treatment, row.id);
                      }}
                      onCancel={() => void removePlan(row.id)}
                    />
                  ) : (
                    <HistoryEntryEditor
                      identity={row.id}
                      storedValue={resolveTreatmentLabel(row)}
                      onSave={savePlanTreatment}
                      label="Planned treatment"
                      placeholder="Add treatment…"
                      voiceEnabled={voiceSettings.voiceToTextEnabled}
                      autoRowsMax={1}
                    />
                  )}
                </div>

                {!isDraft && row.service_id && (
                  <span
                    title="Linked to a catalogue service. Billing can add it; nothing is charged here."
                    style={{
                      marginTop: FIRST_LINE_OFFSET,
                      padding: '1px 6px',
                      borderRadius: 'var(--radius-full)',
                      background: 'var(--bg-surface-2)',
                      color: 'var(--text-muted)',
                      fontSize: 'calc(var(--font-size-xs) * 0.85)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Service
                  </span>
                )}

                {!isDraft && (
                  <select
                    value={status}
                    onChange={(e) =>
                      void setStatus(row.id, e.target.value as DentalTreatmentStatus)
                    }
                    aria-label="Treatment status"
                    style={{
                      marginTop: FIRST_LINE_OFFSET,
                      padding: '2px 6px',
                      borderRadius: 'var(--radius-sm)',
                      border: '1px solid var(--border)',
                      background: 'var(--bg-surface)',
                      color: 'var(--text-muted)',
                      fontSize: 'calc(var(--font-size-xs) * 0.9)',
                      fontFamily: 'inherit',
                      cursor: 'pointer',
                    }}
                  >
                    {ACTIVE_PLAN_STATUSES.concat(['done', 'cancelled']).map((s) => (
                      <option key={s} value={s}>
                        {DENTAL_TREATMENT_STATUS_LABELS[s]}
                      </option>
                    ))}
                  </select>
                )}

                {!isDraft && (
                  <button
                    onClick={() => recordPerformed(row)}
                    title="Record what was performed at this visit"
                    style={{
                      marginTop: FIRST_LINE_OFFSET,
                      padding: '2px 8px',
                      borderRadius: 'var(--radius-sm)',
                      border: `1px solid ${primary}`,
                      background: 'transparent',
                      color: primary,
                      fontSize: 'calc(var(--font-size-xs) * 0.9)',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Record done
                  </button>
                )}

                <button
                  onClick={() => void removePlan(row.id)}
                  aria-label={isDraft ? 'Discard treatment' : 'Cancel treatment'}
                  title={isDraft ? 'Discard' : 'Cancel treatment'}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    marginTop: FIRST_LINE_OFFSET + 4,
                    padding: 4,
                    border: 'none',
                    background: 'transparent',
                    color: 'var(--text-muted)',
                    cursor: 'pointer',
                  }}
                >
                  <NavIcon name="x" />
                </button>
              </div>

              {/* Multi-visit progress: each performed record stays attached to
                    the encounter where the work happened (PRD §11). */}
              {visits.length > 0 && (
                <div
                  style={{
                    paddingLeft: 76,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    fontSize: 'calc(var(--font-size-xs) * 0.9)',
                    color: 'var(--text-muted)',
                  }}
                >
                  {visits.map((v) => (
                    <span key={v.id}>
                      {formatVisitDay(v.performed_at ?? v.created_at)} · {v.treatment}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {addButton('Add Treatment', () => startPlanDraft([]))}
      </div>

      {/* ── Treatment Done ──
          A rule and generous space above it: Plan and Done are two distinct
          clinical records, and at a glance the eye must not read a performed
          entry as a planned one. */}
      <div
        style={{
          marginTop: 'var(--sp-2)',
          paddingTop: 'var(--sp-4)',
          borderTop: '1px solid var(--border)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-4)',
        }}
      >
        <SubsectionHeading>Treatment Done</SubsectionHeading>
        {suggestionStrip((teeth) => startDoneDraft(teeth))}
        {doneRows.length === 0 && (
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
            No treatment recorded for this visit.
          </div>
        )}

        {doneRows.map(({ row, isDraft }) => {
          const teeth = isDraft ? (doneDraft?.teeth ?? []) : (recordTeeth.get(row.id) ?? []);
          const fromPlan = isDraft
            ? doneDraft?.treatmentId != null
            : row.dental_treatment_id != null;
          return (
            <div
              key={row.id}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                paddingBottom: 'var(--sp-2)',
                borderBottom: '1px solid var(--border)',
              }}
            >
              <div
                className="pc-finding-row"
                style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--sp-2)' }}
              >
                {toothButton('record', row.id, teeth, isDraft)}

                <div style={{ flex: 1, minWidth: 0 }}>
                  {isDraft ? (
                    <TreatmentEntryInput
                      services={serviceRows}
                      placeholder="Search services or enter treatment…"
                      primary={primary}
                      autoFocus
                      // Seeded by "Record done" so the plan's treatment and its
                      // service carry straight through (PRD path A).
                      initialText={doneDraft?.id === row.id ? doneDraft.treatment : ''}
                      initialServiceId={doneDraft?.id === row.id ? doneDraft.serviceId : null}
                      onCommit={(value: TreatmentEntryValue) => {
                        // Record the catalogue reference (or null) alongside the
                        // snapshotted label, then persist through the normal path.
                        const pending = pendingRef.current.get(row.id);
                        if (pending) pending.serviceId = value.serviceId;
                        void saveRecordTreatment(value.treatment, row.id);
                      }}
                      onCancel={() => void removeRecord(row.id)}
                    />
                  ) : (
                    <HistoryEntryEditor
                      identity={row.id}
                      storedValue={resolveTreatmentLabel(row)}
                      onSave={saveRecordTreatment}
                      label="Treatment performed"
                      placeholder="Treatment performed…"
                      voiceEnabled={voiceSettings.voiceToTextEnabled}
                      autoRowsMax={1}
                    />
                  )}
                </div>

                {!isDraft && row.service_id && (
                  <span
                    title="Linked to a catalogue service. Billing can add it; nothing is charged here."
                    style={{
                      marginTop: FIRST_LINE_OFFSET,
                      padding: '1px 6px',
                      borderRadius: 'var(--radius-full)',
                      background: 'var(--bg-surface-2)',
                      color: 'var(--text-muted)',
                      fontSize: 'calc(var(--font-size-xs) * 0.85)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Service
                  </span>
                )}

                {fromPlan && (
                  <span
                    title="Recorded against a treatment plan item"
                    style={{
                      marginTop: FIRST_LINE_OFFSET,
                      padding: '1px 6px',
                      borderRadius: 'var(--radius-full)',
                      background: 'var(--bg-surface-2)',
                      color: 'var(--text-muted)',
                      fontSize: 'calc(var(--font-size-xs) * 0.85)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    From plan
                  </span>
                )}

                <button
                  onClick={() => void removeRecord(row.id)}
                  aria-label="Remove treatment record"
                  title="Remove"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    marginTop: FIRST_LINE_OFFSET + 4,
                    padding: 4,
                    border: 'none',
                    background: 'transparent',
                    color: 'var(--text-muted)',
                    cursor: 'pointer',
                  }}
                >
                  <NavIcon name="x" />
                </button>
              </div>

              {/* Notes attach to a saved record only — there is nothing to
                    write them to until the treatment itself is stored. */}
              {!isDraft && (
                <div style={{ paddingLeft: 76, paddingRight: 32 }}>
                  <HistoryEntryEditor
                    identity={`${row.id}:notes`}
                    storedValue={row.notes ?? ''}
                    onSave={(text) => saveRecordNotes(text, row.id)}
                    label="Treatment notes"
                    placeholder="Notes…"
                    voiceEnabled={voiceSettings.voiceToTextEnabled}
                    autoRowsMax={4}
                  />
                </div>
              )}
            </div>
          );
        })}

        {addButton('Add Treatment Done', () => startDoneDraft())}
      </div>

      {picker && (
        <ToothPickerModal
          open
          initialTeeth={pickerTeeth.map((tooth) => ({ tooth, surfaces: [] }))}
          toothStates={chartRows[0] ? parseToothStatesMap(chartRows[0].tooth_states) : {}}
          primary={primary}
          // PRD §15 — teeth only; surface context stays on the chart.
          showSurfaces={false}
          onClose={() => setPicker(null)}
          onSave={(teeth) => {
            const picked = teeth.map((t) => t.tooth);
            const pending = pendingRef.current.get(picker.id);
            if (pending) {
              pending.teeth = picked;
              if (picker.kind === 'plan') {
                setPlanDraft((d) => (d?.id === picker.id ? { ...d, teeth: picked } : d));
              } else {
                setDoneDraft((d) => (d?.id === picker.id ? { ...d, teeth: picked } : d));
              }
            } else {
              void saveTeeth(picker.kind, picker.id, picked);
            }
            setPicker(null);
          }}
        />
      )}
    </div>
  );
}

/** Short day label for the per-visit progress list. */
function formatVisitDay(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
